import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Mail,
  Phone,
  MapPin,
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
  Clock,
  HelpCircle,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";

// ✅ Working Apps Script URL
const APPS_SCRIPT_URL =
  "https://script.google.com/macros/s/AKfycbzeqbLecixjcbv6CB9NGxqQl_xBfFxFSIB3obnWot6VB3qWnwaVjOqExNxlVS6DDf139g/exec";

// WhatsApp Icon Component (unchanged)
const WhatsAppIcon = ({ width = "1.5rem", height = "1.5rem", className = "" }) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347" />
  </svg>
);

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    service: "",
    message: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState("");
  const [submitError, setSubmitError] = useState("");

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // ✅ FIXED SUBMIT HANDLER (from working code)
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus("");
    setSubmitError("");

    const payload = {
      name: formData.name,
      phone: formData.phone,
      email: formData.email,
      course: formData.service, // 🔥 mapping fixed
      message: formData.message,
    };

    try {
      const response = await fetch(APPS_SCRIPT_URL, {
        method: "POST",
        headers: {
          "Content-Type": "text/plain;charset=utf-8",
        },
        body: JSON.stringify(payload),
        redirect: "follow",
      });

      const text = await response.text();

      if (text === "OK") {
        setSubmitStatus("success");
        setFormData({
          name: "",
          phone: "",
          email: "",
          service: "",
          message: "",
        });
      } else {
        setSubmitStatus("error");
        setSubmitError(text);
      }
    } catch (error) {
      setSubmitStatus("error");
      setSubmitError("Network error. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const serviceOptions = [
    "Language Courses - German",
    "Language Courses - Spanish",
    "Language Courses - Japanese",
    "Language Courses - Korean",
    "Language Courses - French",
    "Professional Interpretation",
    "Translation Services",
    "Editing & Content Moderation",
    "Transcription & Subtitling",
    "B2B Language Solutions",
    "Corporate Training",
    "Free Consultation",
    "Other - Please specify",
  ];
  const faqs = [
    {
      question: "How long does it take to complete a language course?",
      answer:
        "Course duration varies by level and language. Most courses range from 12-24 weeks, depending on the complexity and your chosen pace.",
    },
    {
      question: "Do you offer corporate training for teams?",
      answer:
        "Yes, we provide comprehensive B2B language solutions including corporate team training, customized curricula, and enterprise-level support.",
    },
    {
      question: "What certifications do you provide?",
      answer:
        "We offer industry-recognized certifications for language proficiency, cultural competency, and professional translation services.",
    },
    {
      question: "Can I get interpretation services for urgent projects?",
      answer:
        "Yes, we offer 24/7 interpretation services for urgent business needs. Contact us directly for immediate assistance.",
    },
  ];

  const contactMethods = [
    {
      icon: <Phone style={{ width: '1.5rem', height: '1.5rem' }} />,
      title: "Phone Support",
      details: "+91 6377393690",
      description: "Available Monday to Friday, 9 AM - 6 PM EST",
    },
    {
      icon: <Mail style={{ width: '1.5rem', height: '1.5rem' }} />,
      title: "Email",
      details: "info@universityoflanguages.com",
      description: "We respond within 24 hours",
    },
    {
      icon: <WhatsAppIcon width="1.5rem" height="1.5rem" />,
      title: "WhatsApp",
      details: "+91 6377393690",
      description: "Quick responses for urgent inquiries",
    },
    {
      icon: <MapPin style={{ width: '1.5rem', height: '1.5rem' }} />,
      title: "Address",
      details: "56/2 ,ITC ROAD, Kammanahalli Main Rd, Ramaswamipalya",
      description: "Bengaluru, Karnataka 560033",
    },
  ];
  return (
    <div className="contact-page">
      <Header />

      {/* Page Header */}
      <section className="page-header">
        <div className="header-content">
          <h1><span className="first-word">Contact Us </span> </h1>
          <p>
            Ready to start your language learning journey? Get in touch with our
            expert team today
          </p>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="contact-methods-section">
        <div className="section-content">
          <div className="section-header">
            <h2>Get In Touch</h2>
            <p>Choose the contact method that works best for you</p>
          </div>
          <div className="methods-grid">
            {contactMethods.map((method, index) => (
              <div key={index} className="method-card">
                <div className="icon-wrapper">{method.icon}</div>
                <h3>{method.title}</h3>
                <p className="details">{method.details}</p>
                <p>{method.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

{/* Contact Form & Map */}
<section id="contact-form" className="contact-form-section">
        <div className="section-content">
          <div className="form-grid">
            <div className="form-container">
              <h2>Send Us a Message</h2>
              {submitStatus === 'success' && (
                <div className="form-message success">
                  Thank you for your message! We will get back to you within 24 hours.
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="form-message error">
                  There was an error submitting your message. Please try again or contact us directly.
                </div>
              )}
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="name">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter your full name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone">Phone Number *</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+91 6377393690"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="your.email@example.com"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="service">Select Service/Course *</label>
                  <select
                    id="service"
                    name="service"
                    required
                    value={formData.service}
                    onChange={handleInputChange}
                  >
                    <option value="">Choose a service or course</option>
                    {serviceOptions.map((option, index) => (
                      <option key={index} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="message">Message</label>
                  <textarea
                    id="message"
                    name="message"
                    rows={6}
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Tell us about your language learning goals, specific requirements, or any questions you have..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn btn-primary btn-full"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>

            <div className="info-container">
              <h2>Visit Our Campus</h2>

              <div className="campus-image">
                <img 
                  src="/about.png" 
                  alt="University Campus"
                  loading="lazy"
                />
              </div>

              <div className="business-hours">
                <div className="hours-header">
                  <Clock className="icon" style={{ width: '1.5rem', height: '1.5rem' }} />
                  <h3>Business Hours</h3>
                </div>
                <div className="hours-availability">
                  <span className="availability-badge">Available 24×7</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="section-content">
          <div className="section-header">
            <h2>Frequently Asked Questions</h2>
            <p>Quick answers to common questions about our services</p>
          </div>
          <div className="faq-list">
            {faqs.map((faq, index) => (
              <div key={index} className="faq-item">
                <div className="faq-content">
                  <HelpCircle className="icon" style={{ width: '1.5rem', height: '1.5rem' }} />
                  <div className="faq-text">
                    <h3>{faq.question}</h3>
                    <p>{faq.answer}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
